import { resumeRoomSession } from "./roomResume";
import { writeStoredSession } from "../../utils/storedSession";
import { readRoomRecents, recordRoomRecent, hideRoomRecent } from "../../utils/roomRecents";
import { reportMatchJoined } from "../../features/analytics/bridge/lobbyBridge";
import { normalizeMatchRoomCode } from "../../utils/matchRoomCode";
import { readPendingArenaInvitation } from "../../domain/arenaInvitation/pendingArenaInvitation";

/**
 * True when `current` should be replaced by the live session URL.
 * List and profile pages stay on the stack so Back returns to them.
 * @param {string} current
 * @param {string} nextPath
 */
export function shouldReplaceSessionEntry(current, nextPath) {
  const currentPath = String(current || "").split("?")[0];
  const nextBase = String(nextPath || "").split("?")[0];
  if (!currentPath) return false;
  if (currentPath === nextBase) return true;
  if (nextBase.startsWith(`${currentPath}/`) && currentPath.startsWith("/room/")) return true;
  if (/^\/arena\/[^/]+/.test(currentPath)) return true;
  if (/^\/tournaments\/(?!new(?:\/|$))[^/]+/.test(currentPath)) return true;
  if (/^\/competitions\/[^/]+/.test(currentPath)) return true;
  return false;
}

/**
 * One-click re-entry for creators, deputies, and users with an existing access grant.
 * Writes storedSession then navigates to Room Session Shell — no password prompt.
 *
 * @param {{
 *   roomCode: string;
 *   uid?: string | null;
 *   navigate: (to: string, opts?: object) => void;
 *   hideRecentOnGone?: boolean;
 *   tab?: string | null;
 * }} args
 * @returns {Promise<{
 *   ok: boolean;
 *   reason?: string;
 *   needsJoin?: boolean;
 *   gone?: boolean;
 *   room?: string;
 *   roomDisplayName?: string;
 * }>}
 */
export async function enterResumedRoomSession({
  roomCode,
  uid,
  navigate,
  hideRecentOnGone = true,
  tab = null,
}) {
  const room = normalizeMatchRoomCode(roomCode);
  if (!room || typeof navigate !== "function") {
    return { ok: false, reason: "invalid" };
  }

  const hint = uid
    ? (readRoomRecents(uid) || []).find((e) => e.room === room && !e.hidden)
    : null;

  const result = await resumeRoomSession(room, {
    creatorPlaysInSession: hint?.creatorPlaysInSession,
  });

  if (!result.ok) {
    const needsJoin =
      result.reason === "wrong_code" ||
      result.reason === "spectator_code_required" ||
      result.reason === "denied";
    if (needsJoin && !hint?.isCreator) {
      // Invite deep-link: never dump into access-code form — finish via /join/:id.
      const pending = readPendingArenaInvitation();
      if (pending?.invitationId) {
        const joinPath =
          pending.targetRoute ||
          `/join/${encodeURIComponent(pending.invitationId)}`;
        navigate(joinPath, { replace: true });
        return {
          ok: false,
          reason: result.reason,
          needsJoin: true,
          room,
          viaInvite: true,
        };
      }
      navigate(`/play?join=1&room=${encodeURIComponent(room)}`);
      return { ok: false, reason: result.reason, needsJoin: true, room };
    }
    // Don't treat a local Firestore cache crash as a gone room.
    if (result.reason === "firestore_error") {
      return {
        ok: false,
        reason: "firestore_error",
        error: result.error,
        room,
        roomDisplayName: hint?.roomDisplayName || room,
      };
    }
    if (hideRecentOnGone && uid) {
      hideRoomRecent(uid, room);
    }
    return {
      ok: false,
      reason: result.reason || "gone",
      gone: true,
      room,
      roomDisplayName: hint?.roomDisplayName || room,
    };
  }

  writeStoredSession({
    room: result.room,
    role: result.role,
    roomDisplayName: result.roomDisplayName,
    view: "match",
    ...(result.registerAsCreator ? { registerAsCreator: true } : {}),
    ...(result.registerAsCreator && result.creatorPlaysInSession === false
      ? { creatorPlaysInSession: false }
      : {}),
  });

  if (uid) {
    recordRoomRecent(uid, {
      room: result.room,
      roomDisplayName: result.roomDisplayName,
      role: result.role,
      isCreator: Boolean(result.isCreator),
      creatorPlaysInSession: result.creatorPlaysInSession !== false,
    });
  }

  reportMatchJoined(result.role, {
    resume: true,
    registerAsCreator: Boolean(result.registerAsCreator),
    isDeputy: Boolean(result.isDeputy),
  });

  const tabQs =
    tab && String(tab).trim()
      ? `?tab=${encodeURIComponent(String(tab).trim())}`
      : "";
  const path = `/room/${encodeURIComponent(result.room)}/session${tabQs}`;
  const current =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "";
  // Same URL replace remounts Players and looks like a refresh loop.
  // Replace only when this URL is a gateway hop (waiting room, /arena/:id, legacy
  // tournament path). A real page such as Tournaments or Discover must stay in
  // history so Back returns there.
  if (current !== path) {
    if (shouldReplaceSessionEntry(current, path)) {
      navigate(path, { replace: true });
    } else {
      navigate(path);
    }
  }
  return {
    ok: true,
    room: result.room,
    roomDisplayName: result.roomDisplayName,
  };
}
